import { useEffect, useRef, useState } from "react";
import { Settings } from "lucide-react";
import { ghostBtnClass } from "@/components/manufacturing/manufacturingSurfaceClasses";
import type { MfgRecipeKind } from "@/lib/manufacturing/types";
import {
  allLaborRateRecipeKindsSelected,
  LABOR_RATE_RECIPE_KIND_CHOICES,
  toggleLaborRateRecipeKind,
  toggleLaborRateRecipeKindSelectAll,
} from "@/lib/manufacturing/laborCompanyRateRecipe";

export function ManufacturingLaborCompanyRateKindMenu({
  kinds,
  onChange,
  disabled,
}: {
  kinds: ReadonlySet<MfgRecipeKind>;
  onChange: (next: Set<MfgRecipeKind>) => void;
  disabled?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const allSelected = allLaborRateRecipeKindsSelected(kinds);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e: MouseEvent) => {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);

  return (
    <div ref={rootRef} className="relative shrink-0">
      <button
        type="button"
        className={`inline-flex items-center justify-center ${ghostBtnClass}`}
        aria-label="Match key search settings"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
      >
        <Settings className="h-4 w-4" aria-hidden />
      </button>
      {open ? (
        <div className="absolute left-0 z-20 mt-1 w-64 rounded-xl border border-gray-200 bg-white p-3 shadow-lg dark:border-zinc-700 dark:bg-zinc-900">
          <p className="mb-2 text-xs font-semibold text-gray-900 dark:text-zinc-100">Search match key in</p>
          <label className="mb-2 flex items-center gap-2 border-b border-gray-100 pb-2 text-xs text-gray-800 dark:border-zinc-800 dark:text-zinc-200">
            <input
              type="checkbox"
              checked={allSelected}
              ref={(el) => {
                if (el) el.indeterminate = !allSelected && kinds.size > 0;
              }}
              onChange={(e) => onChange(toggleLaborRateRecipeKindSelectAll(e.target.checked))}
            />
            Select all
          </label>
          <ul className="space-y-1.5">
            {LABOR_RATE_RECIPE_KIND_CHOICES.map((choice) => (
              <li key={choice.id}>
                <label className="flex items-center gap-2 text-xs text-gray-800 dark:text-zinc-200">
                  <input
                    type="checkbox"
                    checked={kinds.has(choice.id)}
                    onChange={(e) => onChange(toggleLaborRateRecipeKind(kinds, choice.id, e.target.checked))}
                  />
                  {choice.label}
                </label>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </div>
  );
}
